import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Package,
  Tags,
  Settings,
  MessageSquare,
  LogOut,
  ExternalLink,
  Activity,
  FileText,
  Menu,
  X,
} from "lucide-react";
import api, { authStorage } from "@/lib/api";
import Logo from "@/components/Logo";

const NAV = [
  { to: "/admin", label: "Panel", icon: LayoutDashboard, end: true },
  { to: "/admin/sayfalar", label: "Sayfa İçerikleri", icon: FileText },
  { to: "/admin/urunler", label: "Ürünler", icon: Package },
  { to: "/admin/kategoriler", label: "Kategoriler", icon: Tags },
  { to: "/admin/mesajlar", label: "Mesajlar", icon: MessageSquare },
  { to: "/admin/kayitlar", label: "İşlem Kayıtları", icon: Activity },
  { to: "/admin/ayarlar", label: "Ayarlar", icon: Settings },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (!authStorage.token) {
      navigate("/admin/login");
      return;
    }
    api.get("/auth/me").catch(() => {
      authStorage.clear();
      navigate("/admin/login");
    });
  }, [navigate]);

  // Close drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const logout = () => {
    authStorage.clear();
    navigate("/admin/login");
  };

  const SidebarContent = (
    <>
      <div className="p-6 border-b border-white/10">
        <Link to="/admin" data-testid="admin-logo-link">
          <Logo variant="light" size="md" />
        </Link>
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                isActive ? "bg-[#0066FF] text-white" : "text-white/70 hover:bg-white/5 hover:text-white"
              }`
            }
          >
            <n.icon size={18} />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-white/10 space-y-1">
        <Link
          to="/"
          target="_blank"
          className="flex items-center gap-3 px-4 py-3 text-sm text-white/70 hover:text-white transition-colors"
          data-testid="admin-view-site"
        >
          <ExternalLink size={18} />
          Siteyi Görüntüle
        </Link>
        <button
          onClick={logout}
          className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/70 hover:text-white transition-colors"
          data-testid="admin-logout"
        >
          <LogOut size={18} />
          Çıkış
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-layout">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 bg-[#0A2540] text-white flex-col fixed h-screen z-40">
        {SidebarContent}
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-40 bg-[#0A2540] text-white flex items-center justify-between px-4 h-16 border-b border-white/10">
        <Link to="/admin" className="flex items-center" data-testid="admin-mobile-logo">
          <Logo variant="light" size="sm" />
        </Link>
        <button
          onClick={() => setDrawerOpen(true)}
          className="p-2 -mr-2"
          aria-label="Menu"
          data-testid="admin-drawer-open"
        >
          <Menu size={22} />
        </button>
      </header>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="admin-drawer">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setDrawerOpen(false)}
            data-testid="admin-drawer-overlay"
          />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-[#0A2540] text-white flex flex-col animate-slide-in-right">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <Logo variant="light" size="sm" />
              <button
                onClick={() => setDrawerOpen(false)}
                className="p-2 -mr-2"
                aria-label="Close"
                data-testid="admin-drawer-close"
              >
                <X size={22} />
              </button>
            </div>
            <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                      isActive ? "bg-[#0066FF] text-white" : "text-white/70 hover:bg-white/5 hover:text-white"
                    }`
                  }
                >
                  <n.icon size={18} />
                  {n.label}
                </NavLink>
              ))}
            </nav>
            <div className="p-3 border-t border-white/10 space-y-1">
              <Link
                to="/"
                target="_blank"
                className="flex items-center gap-3 px-4 py-3 text-sm text-white/70 hover:text-white transition-colors"
              >
                <ExternalLink size={18} />
                Siteyi Görüntüle
              </Link>
              <button
                onClick={logout}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/70 hover:text-white transition-colors"
              >
                <LogOut size={18} />
                Çıkış
              </button>
            </div>
          </aside>
        </div>
      )}

      <main className="lg:ml-64 p-4 sm:p-6 lg:p-8">
        <Outlet />
      </main>
    </div>
  );
}
